import { isEmpty, omit } from "lodash"
import React, { MouseEventHandler, useEffect, useState } from "react"
import { Button, Col, Form, Modal, Row } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import PhoneInput from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
import { GroupModel } from "../../api/group.api"
import { WarehouseModel } from "../../api/warehouse.api"

const AddUserModal = ({
    show,
    loading,
    userDetails,
    setUserDetails,
    handleClose,
    handleSubmit,
    checkIdentityValidity,
    groups: allGroups,
    warehouses: allWarehouses,
    errors,
    setErrors
}: {
    show: boolean
    loading: boolean
    userDetails: Record<string, any>
    setUserDetails: Function
    handleClose: () => void
    handleSubmit: Function
    checkIdentityValidity: Function
    groups: GroupModel[]
    warehouses: WarehouseModel[]
    errors: Record<string, any>
    setErrors: Function
}) => {
    const { t, i18n } = useTranslation()
    const classNameForPhoneInput = `${i18n.language === "ar" ? "ltr-direction w-100" : "w-100"} ${errors?.phone ? "is-invalid" : ""}`
    const [isDisabled, setIsDisabled] = useState(false)
    const checkFields = () => {
        if (
            !loading &&
            userDetails?.full_name &&
            userDetails?.full_name !== "" &&
            userDetails.username &&
            userDetails.username !== "" &&
            userDetails.email &&
            userDetails.email !== "" &&
            ((userDetails.password && userDetails.password !== "") ||
                (userDetails?.id && userDetails?.id !== "")) &&
            userDetails?.phone &&
            userDetails?.phone !== "" &&
            isEmpty(errors)
        ) {
            return setIsDisabled(false)
        } else {
            return setIsDisabled(true)
        }
    }

    const handlePhoneInput = (phoneValue: string) => {
        setUserDetails({ ...userDetails, phone: phoneValue })
        if (!!errors["phone"]) setErrors(omit(errors, ["phone", "genericError"]))
    }

    useEffect(() => {
        checkFields()
    }, [
        loading,
        userDetails.id,
        userDetails.full_name,
        userDetails.username,
        userDetails.password,
        userDetails.phone,
        userDetails.email,
        isEmpty(errors)
    ])

    return (
        <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{t("Add New User")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    {errors["genericError"] && (
                        <div className="alert alert-danger">{errors["genericError"]}</div>
                    )}
                    <Row>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="userFullName">{t("User Full Name")}</Form.Label>
                                <Form.Control
                                    id="userFullName"
                                    name={"full_name"}
                                    autoComplete="off"
                                    value={userDetails.full_name}
                                    onChange={(e: any) => {
                                        setUserDetails({ ...userDetails, [e.target.name]: e.target.value })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="User full name..."
                                    minLength={4}
                                    isInvalid={!!errors?.full_name}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.full_name}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="username">{t("Username")}</Form.Label>
                                <Form.Control
                                    id="username"
                                    name={"username"}
                                    onBlur={(e: Record<string, any>) => {
                                        if (
                                            (e.target.value && e.target.value !== "") ||
                                            (userDetails.id && e.target.value !== userDetails.username)
                                        )
                                            checkIdentityValidity(e.target.value, e.target.name)
                                    }}
                                    autoComplete="off"
                                    value={userDetails.username}
                                    onChange={(e: Record<string, any>) => {
                                        setUserDetails({ ...userDetails, [e.target.name]: e.target.value })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="Username..."
                                    minLength={6}
                                    isInvalid={!!errors?.username}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.username}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="userEmail">{t("User Email")}</Form.Label>
                                <Form.Control
                                    id="userEmail"
                                    type={"email"}
                                    name={"email"}
                                    disabled={!!errors?.username}
                                    onBlur={(e: Record<string, any>) => {
                                        if (
                                            (e.target.value && e.target.value !== "") ||
                                            (userDetails.id && e.target.value !== userDetails.email)
                                        )
                                            checkIdentityValidity(e.target.value, e.target.name)
                                    }}
                                    autoComplete="off"
                                    value={userDetails.email}
                                    onChange={(e: Record<string, any>) => {
                                        setUserDetails({ ...userDetails, [e.target.name]: e.target.value })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    placeholder="User email..."
                                    isInvalid={!!errors?.email}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.email}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={6}>
                            <Form.Group className="mb-3">
                                <Form.Label htmlFor="userPhone">{t("User Phone no.")}</Form.Label>
                                <PhoneInput
                                    inputProps={{
                                        name: "phone",
                                        autoComplete: "off",
                                        minLength: 6
                                    }}
                                    country={"ae"}
                                    onChange={handlePhoneInput}
                                    inputClass="w-100"
                                    value={userDetails.phone}
                                    containerClass={classNameForPhoneInput}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.phone}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="d-flex justify-content-center align-items-end">
                        <Col lg={6}>
                            <Form.Group>
                                <Form.Label htmlFor="userPassword">{t("User Password")}</Form.Label>
                                <Form.Control
                                    id="userPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    name={userDetails.id ? "newPassword" : "password"}
                                    value={userDetails.id ? userDetails.newPassword : userDetails.password}
                                    onChange={(e: any) => {
                                        setUserDetails({ ...userDetails, [e.target.name]: e.target.value })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}
                                    minLength={6}
                                    placeholder="User password..."
                                    isInvalid={!!errors?.password}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {t(`${errors.password}`)}
                                </Form.Control.Feedback>
                            </Form.Group>
                        </Col>
                        <Col lg={3}>
                            <Form.Group className="">
                                <Form.Label htmlFor="ParcelHubUserGroup">{t("User Group")}</Form.Label>
                                <Form.Select
                                    aria-label="Group of user"
                                    placeholder="Select a Group"
                                    id="ParcelHubUserGroup"
                                    name="groups"
                                    defaultValue={userDetails?.id ? userDetails?.groups[0] : null}
                                    onChange={(e: any) => {
                                        setUserDetails({
                                            ...userDetails,
                                            [e.target.name]: [Number(e.target.value)]
                                        })
                                        if (!!errors[e.target.name])
                                            setErrors(omit(errors, [e.target.name, "genericError"]))
                                    }}>
                                    <option>{t("Select a group")}</option>
                                    {allGroups.map((group) => (
                                        <option value={group.id}>{group.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col lg={3}>
                            <Form.Group className="d-none">
                                <Form.Check
                                    type="checkbox"
                                    name="active"
                                    label={t("Activate")}
                                    onChange={(e: any) => {
                                        setUserDetails({
                                            ...userDetails,
                                            [e.target.name]: e.target.checked ? 1 : 0
                                        })
                                    }}
                                    checked={userDetails.active}
                                    defaultChecked={true}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        <h3 className="label mb-3">{t("Warehouses Access")}</h3>
                        <Form.Group>
                            <Row>
                                <Col lg={6}>
                                    {allWarehouses.map((warehouse) => (
                                        <Col lg={12} key={`${warehouse.id}_${warehouse.name}`}>
                                            <Form.Check
                                                type="checkbox"
                                                label={warehouse.name}
                                                value={warehouse.id}
                                                defaultChecked={
                                                    userDetails.id &&
                                                    userDetails?.warehouses?.includes(warehouse.id)
                                                }
                                                onChange={(e: any) => {
                                                    if (e.target.checked) {
                                                        setUserDetails({
                                                            ...userDetails,
                                                            warehouses: [
                                                                ...(userDetails.warehouses || []),
                                                                Number(e.target.value)
                                                            ]
                                                        })
                                                    } else {
                                                        if (userDetails?.warehouses?.includes(warehouse.id)) {
                                                            setUserDetails({
                                                                ...userDetails,
                                                                warehouses: (
                                                                    userDetails?.warehouses || []
                                                                )?.filter((w: number) => w !== warehouse.id)
                                                            })
                                                        }
                                                    }
                                                }}
                                            />
                                        </Col>
                                    ))}
                                </Col>
                                <Col lg={6}>
                                    <Form.Group className="">
                                        <Form.Label htmlFor="ParcelHubUserDefWarehouse">
                                            {t("Default Warehouse")}
                                        </Form.Label>
                                        <Form.Select
                                            aria-label="Default warehouse if a user"
                                            placeholder="Select a Default Warehouse"
                                            id="ParcelHubUserDefWarehouse"
                                            name="default_warehouse"
                                            defaultValue={
                                                userDetails?.id
                                                    ? userDetails?.ParcelHubUserWarehouse?.find(
                                                          (record: Record<string, any>) => record?.is_default
                                                      )?.warehouse_id
                                                    : null
                                            }
                                            onChange={(e: any) => {
                                                setUserDetails({
                                                    ...userDetails,
                                                    [e.target.name]: Number(e.target.value)
                                                })
                                                if (!!errors[e.target.name])
                                                    setErrors(omit(errors, [e.target.name, "genericError"]))
                                            }}>
                                            <option>{t("Select a warehouse")}</option>
                                            {userDetails?.warehouses?.map((warehouse: number) => (
                                                <option value={warehouse}>
                                                    {
                                                        allWarehouses?.find(
                                                            (w: Record<string, any>) => w?.id === warehouse
                                                        )?.name
                                                    }
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Form.Group>
                    </Row>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose as MouseEventHandler<HTMLButtonElement>}>
                    {t("Close")}
                </Button>

                <Button
                    disabled={isDisabled}
                    onClick={() => {
                        handleSubmit(userDetails)
                    }}
                    className="btn btn-primary"
                    type="submit">
                    {loading ? (
                        <div className="spinner-border text-light" role="submit"></div>
                    ) : userDetails?.id ? (
                        t("Update")
                    ) : (
                        t("Submit")
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AddUserModal
